import React, {createContext, useContext, useState, useCallback} from 'react';

interface SimpleTourContextType {
    isActive: boolean;
    dismissedTours: Set<string>;
    startTour: () => void;
    stopTour: () => void;
    toggleTour: () => void;
    dismissCurrentTour: () => void;
}

const SimpleTourContext = createContext<SimpleTourContextType | undefined>(undefined);

export const useSimpleTour = () => {
    const context = useContext(SimpleTourContext);
    if (context === undefined) {
        throw new Error('useSimpleTour must be used within a SimpleTourProvider');
    }
    return context;
};

interface SimpleTourProviderProps {
    children: React.ReactNode;
}

export const SimpleTourProvider: React.FC<SimpleTourProviderProps> = ({children}) => {
    // Initialize from localStorage to persist state across page refreshes
    const [isActive, setIsActive] = useState(() => {
        const saved = localStorage.getItem('simpleTourActive');
        return saved ? JSON.parse(saved) : true;
    });

    // Track dismissed tours per page to avoid showing them again in the same session
    const [dismissedTours, setDismissedTours] = useState<Set<string>>(new Set());

    const startTour = useCallback(() => {
        console.log('SimpleTour Provider: Starting tour');
        setIsActive(true);
        localStorage.setItem('simpleTourActive', 'true');
        // Clear dismissed tours when manually starting tour
        setDismissedTours(new Set());
    }, []);

    const stopTour = useCallback(() => {
        console.log('SimpleTour Provider: Stopping tour');
        setIsActive(false);
        localStorage.setItem('simpleTourActive', 'false');
        // Clear dismissed tours when stopping tour
        setDismissedTours(new Set());
    }, []);

    const toggleTour = useCallback(() => {
        console.log('SimpleTour Provider: Toggling tour, current state:', isActive);
        const newState = !isActive;
        setIsActive(newState);
        localStorage.setItem('simpleTourActive', newState.toString());

        // Clear dismissed tours when toggling
        setDismissedTours(new Set());
    }, [isActive]);

    const dismissCurrentTour = useCallback(() => {
        const currentPath = window.location.pathname;
        console.log('SimpleTour Provider: Dismissing current tour for page:', currentPath);
        // Add current page to dismissed tours
        setDismissedTours(prev => new Set([...prev, currentPath]));
        // Note: We don't change isActive - the guide stays on for other pages
    }, []);

    const value = {
        isActive,
        dismissedTours,
        startTour,
        stopTour,
        toggleTour,
        dismissCurrentTour
    };

    console.log('SimpleTour Provider: Rendering with isActive =', isActive, 'dismissed =', Array.from(dismissedTours));

    return (
        <SimpleTourContext.Provider value={value}>
            {children}
        </SimpleTourContext.Provider>
    );
};